import { QueryClient, QueryClientProvider, focusManager } from "@tanstack/react-query"
import { installIdlePause } from "@playside/tab-activity"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { createBrowserRouter, RouterProvider, Outlet, redirect, Navigate } from "react-router-dom"
import { AuthProvider } from "./contexts/AuthContext"
import { Toaster } from "./components/Toast"
import { RouterErrorBoundary } from "./components/ErrorBoundary/RouterErrorBoundary"
import { Login } from "./pages/Login"
import { Dashboard } from "./pages/Dashboard"
import { Offers } from "./pages/Offers"
import { WelcomeOffers } from "./pages/WelcomeOffers"
import { WelcomeOfferDetail } from "./pages/WelcomeOfferDetail"
import { Users } from "./pages/Users"
import { CommsEvents } from "./pages/CommsEvents"
import { UserDetail } from "./pages/UserDetail"
import { PendingRegistrations } from "./pages/PendingRegistrations"
import { Teams } from "./pages/Teams"
import { TeamDetail } from "./pages/TeamDetail"
import { TeamActivity } from "./pages/TeamActivity"
import { Affiliates } from "./pages/Affiliates"
import { AffiliateDetail } from "./pages/AffiliateDetail"
import { AffiliateStatement } from "./pages/AffiliateStatement"
import { Clips } from "./pages/Clips"
import { Games } from "./pages/Games"
import { GameDetail } from "./pages/GameDetail"
import { Diagrams } from "./pages/Diagrams"
import { DiagramTransfer } from "./pages/DiagramTransfer"
import { TeamOwnershipBackfill } from "./pages/TeamOwnershipBackfill"
import { Search } from "./pages/Search"
import { StorageOverview } from "./pages/StorageOverview"
import { SubscriptionPlans } from "./pages/SubscriptionPlans"
import { Subscriptions } from "./pages/Subscriptions"
import { SubscriptionDetail } from "./pages/SubscriptionDetail"
import { ProtectedRoute } from "./components/ProtectedRoute"
import { DevLogin } from "./pages/DevLogin"
import Organizations from "./pages/Organizations"
import OrganizationDetail from "./pages/OrganizationDetail"
import { Uploads } from "./pages/Uploads"
import { UploadDetail } from "./pages/UploadDetail"
import { Observability } from "./pages/Observability"
import { ProductStats } from "./pages/ProductStats"
import { Jobs } from "./pages/Jobs"
import { RecentOperations } from "./pages/RecentOperations"
import { EmailLayout } from "./components/EmailLayout"
import { EmailStats } from "./pages/EmailStats"
import { EmailTemplates } from "./pages/EmailTemplates"
import { EmailCompose } from "./pages/EmailCompose"
import { EmailLog } from "./pages/EmailLog"
import { EmailSetup } from "./pages/EmailSetup"
import { CommsTriage } from "./pages/CommsTriage"
import { CommsContacts } from "./pages/CommsContacts"
import { CommsContactDetail } from "./pages/CommsContactDetail"
import { CommsThread } from "./pages/CommsThread"
import { CommsThreads } from "./pages/CommsThreads"
import { CommsMessages } from "./pages/CommsMessages"
import { CommsOrgs } from "./pages/CommsOrgs"
import { CommsOrgDetail } from "./pages/CommsOrgDetail"
import { CropWaitlist } from "./pages/CropWaitlist"
import { CheerWaitlist } from "./pages/CheerWaitlist"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})
// No page polls while its tab is hidden or idle (2026-09-28 incident) — this makes every
// refetchInterval query pause/resume with the tab's activity state.
installIdlePause(focusManager)

// Root layout component that provides auth context
function RootLayout() {
  return (
    <AuthProvider>
      <Outlet />
      <Toaster />
    </AuthProvider>
  )
}

// Create router with proper error handling
const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: "login",
        element: <Login />,
      },
      ...(import.meta.env.DEV
        ? [
            {
              path: "dev-login",
              element: <DevLogin />,
            },
          ]
        : []),
      {
        path: "dashboard",
        element: (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        ),
      },
      { path: "offers", element: <ProtectedRoute><Offers /></ProtectedRoute> },
      { path: "offers/diagrams", element: <ProtectedRoute><WelcomeOffers /></ProtectedRoute> },
      { path: "offers/diagrams/:userId", element: <ProtectedRoute><WelcomeOfferDetail /></ProtectedRoute> },
      {
        path: "welcome-offer",
        loader: ({ request }) => redirect(`/offers/diagrams${new URL(request.url).search}`),
      },
      {
        path: "welcome-offer/:userId",
        loader: ({ params }) => redirect(`/offers/diagrams/${encodeURIComponent(params.userId!)}`),
      },
      {
        path: "users",
        element: (
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/:userId",
        element: (
          <ProtectedRoute>
            <UserDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: "pending-registrations",
        element: (
          <ProtectedRoute>
            <PendingRegistrations />
          </ProtectedRoute>
        ),
      },
      {
        path: "teams",
        element: (
          <ProtectedRoute>
            <Teams />
          </ProtectedRoute>
        ),
      },
      {
        path: "teams/:teamId",
        element: (
          <ProtectedRoute>
            <TeamDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: "teams/:teamId/activity",
        element: (
          <ProtectedRoute>
            <TeamActivity />
          </ProtectedRoute>
        ),
      },
      {
        path: "affiliates",
        element: (
          <ProtectedRoute>
            <Affiliates />
          </ProtectedRoute>
        ),
      },
      {
        path: "affiliates/:affiliateId",
        element: (
          <ProtectedRoute>
            <AffiliateDetail />
          </ProtectedRoute>
        ),
      },
      {
        // Statements live under their affiliate; the breadcrumb's parent link lands there.
        path: "affiliate-statements",
        element: <Navigate to="/affiliates" replace />,
      },
      {
        path: "affiliate-statements/:statementId",
        element: (
          <ProtectedRoute>
            <AffiliateStatement />
          </ProtectedRoute>
        ),
      },
      {
        path: "games",
        element: (
          <ProtectedRoute>
            <Games />
          </ProtectedRoute>
        ),
      },
      {
        path: "games/:gameId",
        element: (
          <ProtectedRoute>
            <GameDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: "diagrams/transfer",
        element: (
          <ProtectedRoute>
            <DiagramTransfer />
          </ProtectedRoute>
        ),
      },
      {
        path: "diagrams/team-ownership",
        element: (
          <ProtectedRoute>
            <TeamOwnershipBackfill />
          </ProtectedRoute>
        ),
      },
      {
        path: "diagrams",
        element: (
          <ProtectedRoute>
            <Diagrams />
          </ProtectedRoute>
        ),
      },
      {
        path: "clips",
        element: (
          <ProtectedRoute>
            <Clips />
          </ProtectedRoute>
        ),
      },
      {
        path: "search",
        element: (
          <ProtectedRoute>
            <Search />
          </ProtectedRoute>
        ),
      },
      {
        path: "resources/storage",
        element: (
          <ProtectedRoute>
            <StorageOverview />
          </ProtectedRoute>
        ),
      },
      {
        path: "subscription-plans",
        element: (
          <ProtectedRoute>
            <SubscriptionPlans />
          </ProtectedRoute>
        ),
      },
      {
        path: "subscriptions",
        element: (
          <ProtectedRoute>
            <Subscriptions />
          </ProtectedRoute>
        ),
      },
      {
        path: "subscriptions/:subscriptionId",
        element: (
          <ProtectedRoute>
            <SubscriptionDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: "organizations",
        element: (
          <ProtectedRoute>
            <Organizations />
          </ProtectedRoute>
        ),
      },
      {
        path: "organizations/:orgId",
        element: (
          <ProtectedRoute>
            <OrganizationDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: "observability",
        element: (
          <ProtectedRoute>
            <Observability />
          </ProtectedRoute>
        ),
      },
      {
        path: "product-stats",
        element: (
          <ProtectedRoute>
            <ProductStats />
          </ProtectedRoute>
        ),
      },
      {
        path: "uploads",
        element: (
          <ProtectedRoute>
            <Uploads />
          </ProtectedRoute>
        ),
      },
      {
        path: "uploads/:batchId",
        element: (
          <ProtectedRoute>
            <UploadDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: "jobs",
        element: (
          <ProtectedRoute>
            <Jobs />
          </ProtectedRoute>
        ),
      },
      {
        path: "recent-ops",
        element: (
          <ProtectedRoute>
            <RecentOperations />
          </ProtectedRoute>
        ),
      },
      {
        // Email section: one tabbed area. Stats + Templates lead; Log + Setup
        // are secondary. EmailLayout owns the header + tab nav; sub-pages render
        // into its <Outlet />. ProtectedRoute gates the whole section.
        path: "email",
        element: (
          <ProtectedRoute>
            <EmailLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="stats" replace /> },
          { path: "compose", element: <EmailCompose /> },
          { path: "stats", element: <EmailStats /> },
          { path: "templates", element: <EmailTemplates /> },
          { path: "log", element: <EmailLog /> },
          { path: "setup", element: <EmailSetup /> },
        ],
      },
      // Redirect the old flat routes so existing bookmarks keep working.
      { path: "email-previews", loader: () => redirect("/email/templates") },
      // The old "Sending Email" page was primarily the Gmail/SMTP how-to, which
      // now lives on the Setup tab, send those bookmarks/doc links there.
      { path: "email-sending", loader: () => redirect("/email/setup") },
      { path: "email-log", loader: () => redirect("/email/log") },
      {
        path: "comms",
        element: (
          <ProtectedRoute>
            <CommsTriage />
          </ProtectedRoute>
        ),
      },
      {
        path: "comms/events",
        element: (
          <ProtectedRoute>
            <CommsEvents />
          </ProtectedRoute>
        ),
      },
      {
        path: "comms/contacts",
        element: (
          <ProtectedRoute>
            <CommsContacts />
          </ProtectedRoute>
        ),
      },
      {
        path: "comms/contacts/:publicId",
        element: (
          <ProtectedRoute>
            <CommsContactDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: "comms/threads",
        element: (
          <ProtectedRoute>
            <CommsThreads />
          </ProtectedRoute>
        ),
      },
      {
        path: "comms/messages",
        element: (
          <ProtectedRoute>
            <CommsMessages />
          </ProtectedRoute>
        ),
      },
      {
        path: "comms/threads/:publicId",
        element: (
          <ProtectedRoute>
            <CommsThread />
          </ProtectedRoute>
        ),
      },
      {
        path: "comms/orgs",
        element: (
          <ProtectedRoute>
            <CommsOrgs />
          </ProtectedRoute>
        ),
      },
      {
        path: "comms/orgs/:publicId",
        element: (
          <ProtectedRoute>
            <CommsOrgDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: "comms/crop-waitlist",
        element: (
          <ProtectedRoute>
            <CropWaitlist />
          </ProtectedRoute>
        ),
      },
      {
        path: "comms/cheer-waitlist",
        element: (
          <ProtectedRoute>
            <CheerWaitlist />
          </ProtectedRoute>
        ),
      },
      {
        // Legacy alias: the Detect Jobs page (#636) was merged into the unified
        // Jobs page (#649). Keep saved links / runbooks pointing at the old
        // path working by redirecting to the canonical /jobs route.
        path: "detect-jobs",
        loader: () => redirect("/jobs"),
      },
    ],
  },
])

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
